import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, Loader2, Play, Rss, Search, Youtube } from "lucide-react";
import { api } from "@/lib/api";
import { useSEO } from "@/lib/seo";
import { track } from "@/lib/analytics";
import { Reveal, Overline } from "@/components/Reveal";

const FILTERS = [
  { key: "", label: "All Content" },
  { key: "episode", label: "Podcast Episodes" },
  { key: "post", label: "Posts & Articles" },
];

const fmt = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
    : "";

const EpisodeCard = ({ item, big }) => {
  const [watching, setWatching] = useState(false);
  return (
    <div
      data-testid={`media-item-${item.item_id}`}
      className={`flex h-full flex-col overflow-hidden rounded-[2rem] border border-forest/10 bg-white/75 shadow-[0_14px_35px_rgba(23,63,53,0.08)] transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_22px_50px_rgba(23,63,53,0.14)] ${big ? "lg:col-span-2" : ""}`}
    >
      {watching && item.youtube_id ? (
        <div className="aspect-video w-full">
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${item.youtube_id}?autoplay=1&rel=0`}
            title={item.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="h-full w-full"
          />
        </div>
      ) : (
        <button
          data-testid={`media-watch-${item.item_id}`}
          onClick={() => {
            setWatching(true);
            track("media_watch", { item: item.item_id, kind: "episode" });
          }}
          className="group relative block aspect-video w-full overflow-hidden text-left"
          aria-label={`Watch ${item.title}`}
        >
          <img
            src={item.thumbnail}
            alt={`${item.title} video thumbnail`}
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
            loading="lazy"
          />
          <span className="absolute inset-0 flex items-center justify-center bg-forest/25 transition-colors group-hover:bg-forest/15">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-gold text-forest-deep shadow-lg transition-transform duration-300 group-hover:scale-110">
              <Play className="ml-0.5 h-6 w-6 fill-current" />
            </span>
          </span>
        </button>
      )}
      <div className="flex flex-1 flex-col p-6">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gold-dark">
          {fmt(item.published_at)}
        </p>
        <h3 className="mt-2 font-serif text-xl leading-snug text-forest">{item.title}</h3>
        {item.description && (
          <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-charcoal/70">
            {item.description}
          </p>
        )}
        {item.topics?.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {item.topics.map((t) => (
              <span key={t} className="rounded-full bg-sage px-3 py-1 text-[11px] font-semibold text-forest">
                {t}
              </span>
            ))}
          </div>
        )}
        {!watching && item.youtube_id && (
          <button
            onClick={() => {
              setWatching(true);
              track("media_watch", { item: item.item_id, kind: "episode" });
            }}
            className="mt-auto inline-flex w-fit items-center gap-2 pt-4 text-sm font-semibold text-forest underline-offset-4 hover:underline"
          >
            <Play className="h-4 w-4" />
            Watch Video
          </button>
        )}
      </div>
    </div>
  );
};

const PostCard = ({ item }) => (
  <div
    data-testid={`media-item-${item.item_id}`}
    className="flex h-full flex-col overflow-hidden rounded-[2rem] border border-forest/10 bg-white/75 shadow-[0_14px_35px_rgba(23,63,53,0.08)] transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_22px_50px_rgba(23,63,53,0.14)]"
  >
    {item.featured_image && (
      <div className="aspect-[16/9] w-full overflow-hidden">
        <img
          src={item.featured_image}
          alt={`${item.title} featured image`}
          className="h-full w-full object-cover"
          loading="lazy"
        />
      </div>
    )}
    <div className="flex flex-1 flex-col p-6">
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gold-dark">
        {fmt(item.published_at)}
      </p>
      <h3 className="mt-2 font-serif text-xl leading-snug text-forest">{item.title}</h3>
      {(item.excerpt || item.description) && (
        <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-charcoal/70">
          {item.excerpt || item.description}
        </p>
      )}
      {item.external_url ? (
        <a
          href={item.external_url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`media-read-${item.item_id}`}
          className="mt-auto inline-flex w-fit items-center gap-2 pt-4 text-sm font-semibold text-forest underline-offset-4 hover:underline"
        >
          <Rss className="h-4 w-4" />
          Read More
        </a>
      ) : (
        <Link
          to={`/terrain-and-truth/post/${item.item_id}`}
          data-testid={`media-read-${item.item_id}`}
          className="mt-auto inline-flex w-fit items-center gap-2 pt-4 text-sm font-semibold text-forest underline-offset-4 hover:underline"
        >
          <Rss className="h-4 w-4" />
          Read More
        </Link>
      )}
    </div>
  </div>
);

export default function TerrainAndTruth() {
  const [data, setData] = useState(null);
  const [kind, setKind] = useState("");
  const [topic, setTopic] = useState("");
  const [q, setQ] = useState("");
  const [shown, setShown] = useState(9);

  useSEO(
    "Terrain and Truth | Rooted Healing",
    "Staci Freed's Terrain and Truth: podcast episodes, conversations, and written posts on holistic wellness and conscious living."
  );

  useEffect(() => {
    track("terrain_view");
    api
      .get("/content/media")
      .then(({ data }) => setData(data))
      .catch(() => setData({ items: [], topics: [], channel_url: "" }));
  }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    const needle = q.trim().toLowerCase();
    return data.items.filter((i) => {
      if (kind && i.kind !== kind) return false;
      if (topic && !(i.topics || []).includes(topic)) return false;
      if (needle && !`${i.title} ${i.description} ${i.excerpt}`.toLowerCase().includes(needle))
        return false;
      return true;
    });
  }, [data, kind, topic, q]);

  const visible = filtered.slice(0, shown);

  return (
    <main data-testid="terrain-page" className="pt-[76px]">
      <section className="mx-auto max-w-5xl px-5 py-16 text-center sm:px-8 sm:py-20">
        <Reveal>
          <Overline>Podcast & Writings</Overline>
          <h1 className="mt-4 font-serif text-4xl leading-tight text-forest sm:text-5xl">
            Terrain and <em className="italic text-gold">Truth</em>
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-lg font-light leading-relaxed text-charcoal/70">
            Honest conversations and thoughtful writing on holistic wellness, conscious living,
            and the everyday terrain of feeling well. Watch, listen, and read at your own pace.
          </p>
          {data?.channel_url && (
            <a
              href={data.channel_url.startsWith("http") ? data.channel_url : `https://www.youtube.com/${data.channel_url}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="terrain-subscribe-youtube"
              className="mt-7 inline-flex items-center gap-2 rounded-full border border-forest/25 px-6 py-3 text-sm font-semibold text-forest transition-colors hover:border-gold hover:text-gold-dark"
            >
              <Youtube className="h-4 w-4" />
              Subscribe on YouTube
            </a>
          )}
        </Reveal>
      </section>

      <section className="mx-auto max-w-6xl px-5 pb-20 sm:px-8">
        <div className="flex flex-col items-center gap-4">
          <div className="flex flex-wrap justify-center gap-2">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                data-testid={`terrain-filter-${f.key || "all"}`}
                onClick={() => {
                  setKind(f.key);
                  setShown(9);
                }}
                className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${
                  kind === f.key
                    ? "bg-forest text-ivory"
                    : "border border-forest/20 text-forest hover:border-gold"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="flex w-full max-w-xl flex-wrap items-center justify-center gap-3">
            <div className="relative min-w-56 flex-1">
              <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-taupe" />
              <input
                data-testid="terrain-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search episodes and posts"
                className="w-full rounded-full border border-forest/20 bg-white/70 py-3 pl-11 pr-5 text-sm outline-none focus:border-gold"
              />
            </div>
          </div>
          {data && data.topics.length > 0 && (
            <div className="flex flex-wrap justify-center gap-2">
              {data.topics.map((t) => (
                <button
                  key={t}
                  data-testid={`terrain-topic-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  onClick={() => {
                    setTopic(topic === t ? "" : t);
                    setShown(9);
                  }}
                  className={`rounded-full px-4 py-2 text-xs font-semibold transition-colors ${
                    topic === t
                      ? "bg-gold/20 text-gold-dark"
                      : "bg-sage text-forest hover:bg-sage-light"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          )}
        </div>

        {!data ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-forest" />
          </div>
        ) : visible.length === 0 ? (
          <div className="mx-auto mt-14 max-w-xl rounded-[2rem] border border-forest/10 bg-white/75 p-10 text-center">
            <p className="font-serif text-2xl text-forest">New content is on its way</p>
            <p className="mt-3 text-sm leading-relaxed text-charcoal/70">
              {data.items.length === 0
                ? "The Terrain and Truth library is being prepared. Check back soon for episodes and articles."
                : "Nothing matches that filter yet. Try another topic or search."}
            </p>
          </div>
        ) : (
          <>
            <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {visible.map((item, i) =>
                item.kind === "episode" ? (
                  <EpisodeCard key={item.item_id} item={item} big={item.featured && i === 0} />
                ) : (
                  <PostCard key={item.item_id} item={item} />
                )
              )}
            </div>
            {filtered.length > shown && (
              <div className="mt-10 text-center">
                <button
                  data-testid="terrain-load-more"
                  onClick={() => setShown((s) => s + 9)}
                  className="rounded-full border border-forest/25 px-8 py-3.5 text-sm font-semibold text-forest transition-colors hover:border-gold"
                >
                  Load More
                </button>
              </div>
            )}
          </>
        )}

        <Reveal>
          <div className="mx-auto mt-16 max-w-2xl rounded-[2rem] bg-forest p-8 text-center sm:p-10">
            <CalendarDays className="mx-auto h-5 w-5 text-gold-soft" />
            <p className="mt-4 font-serif text-xl italic leading-relaxed text-ivory">
              Want to explore what these ideas mean for your own life?
            </p>
            <Link
              to="/wellness-discovery"
              data-testid="terrain-discovery-cta"
              className="mt-5 inline-flex items-center gap-2 rounded-full bg-gold px-7 py-3.5 text-sm font-semibold text-forest-deep transition-colors hover:bg-gold-soft"
            >
              Explore the Wellness Discovery
            </Link>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
